import React, { createContext, useContext, useState } from 'react'
import { Report, ReportRun } from 'src/components/Reports/types'
import { useShareEmail, ShareAccount } from 'src/lib/hooks/useShareEmail'

interface ReportContextType {
  reports: Report[]
  setReports: (reports: Report[]) => void
  pauseReport: (reportId: string, runId: string) => void
  resumeReport: (reportId: string, runId: string) => void
  emails: string[]
  accounts: ShareAccount[]
  isValidating: boolean
  emailError: any
}

const ReportContext = createContext<ReportContextType | undefined>(undefined)

export const ReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reports, setReports] = useState<Report[]>([])
  const { emails, accounts, isValidating, emailError } = useShareEmail()

  const pauseReport = (reportId: string, runId: string) => {
    const report = reports.find(report => report.reportId === reportId)
    if (report) {
      report.runs = report.runs.map(run =>
        run.runId === runId ? { ...run, status: 'PAUSED' as ReportRun['status'] } : run
      )
      setReports([...reports])
    }
  }

  const resumeReport = (reportId: string, runId: string) => {
    const report = reports.find(report => report.reportId === reportId)
    if (report) {
      report.runs = report.runs.map(run =>
        run.runId === runId ? { ...run, status: 'SCHEDULED' as ReportRun['status'] } : run
      )
      setReports([...reports])
    }
  }

  return (
    <ReportContext.Provider
      value={{ reports, setReports, pauseReport, resumeReport, emails, accounts, isValidating, emailError }}
    >
      {children}
    </ReportContext.Provider>
  )
}

export const useReports = () => {
  const context = useContext(ReportContext)
  if (!context) {
    throw new Error('useReports must be used within a ReportProvider')
  }
  return context
}
